import type { RootState } from 'store/rootReducer';
import type { Betreuer } from 'api/betreuer';
import type { ArtikelGruppe, Einheit, Kalkulationsblatt, Lager, Land, Lieferantenformular } from 'api/lookup';
import type { ArtikelEigenschaft } from 'api/artikelEigenschaft';
import type { AraRegelung } from 'api/araRegelung';
import type { Branche } from 'api/branche';
import type { ErweiterteSuche } from 'api/erweiterteSuche';
import type { InteresseFuerProdukte } from 'api/interesseFuerProdukte';
import type { Produktgruppe } from 'api/produktgruppe';
import type { Textbaustein } from 'api/textbaustein';
import type { Skontodefinition } from 'api/skontodefinition';
import type { Kontodefinition } from 'api/kontodefinition';
import type { Produzent } from 'api/produzent';
import type { Maschinentype } from 'api/maschinentype';

import { defaultLookups, Lookups } from './types';

export const lookupsSelector = (state: RootState): Lookups | null => {
    return state?.lookups || null;
};

export const betreuerSelector = (state: RootState): Betreuer[] => {
    if (state?.lookups) {
        return state.lookups?.betreuer || [];
    }

    return defaultLookups.betreuer || [];
};

export const betreuerByNrSelector = (state: RootState): { [key: string]: Betreuer } => {
    if (state?.lookups) {
        return state.lookups?.betreuerByNr || {};
    }

    return defaultLookups.betreuerByNr || {};
};

export const betreuerByKzSelector = (state: RootState): { [key: string]: Betreuer } => {
    if (state?.lookups) {
        return state.lookups?.betreuerByKz || {};
    }

    return defaultLookups.betreuerByNr || {};
};

export const textbausteineSelector = (state: RootState): Textbaustein[] => {
    if (state?.lookups) {
        return state.lookups?.textbausteine || [];
    }

    return defaultLookups.textbausteine || [];
};

export const textbausteineByKzSelector = (state: RootState): { [key: number]: Textbaustein[] } => {
    if (state?.lookups) {
        return state.lookups?.textbausteineByKz || {};
    }

    return defaultLookups.textbausteineByKz || {};
};

export const textbausteineByKzNrSelector = (state: RootState): { [key: number]: { [key: number]: Textbaustein } } => {
    if (state?.lookups) {
        return state.lookups?.textbausteineByKzNr || {};
    }

    return defaultLookups.textbausteineByKzNr || {};
};

export const artikelGruppenSelector = (state: RootState): ArtikelGruppe[] => {
    if (state?.lookups) {
        return state.lookups?.artikelGruppen || [];
    }

    return defaultLookups.artikelGruppen || [];
};

export const artikelGruppenByNrSelector = (state: RootState): { [key: string]: ArtikelGruppe } => {
    if (state?.lookups) {
        return state.lookups?.artikelGruppenByNr || {};
    }

    return defaultLookups.artikelGruppenByNr || {};
};

export const branchenSelector = (state: RootState): Branche[] => {
    if (state?.lookups) {
        return state.lookups?.branchen || [];
    }

    return defaultLookups.branchen || [];
};

export const branchenByNrSelector = (state: RootState): { [key: string]: Branche } => {
    if (state?.lookups) {
        return state.lookups?.branchenByNr || {};
    }

    return defaultLookups.branchenByNr || {};
};

export const interesseFuerProdukteSelector = (state: RootState): InteresseFuerProdukte[] => {
    if (state?.lookups) {
        return state.lookups?.interesseFuerProdukte || [];
    }

    return defaultLookups.interesseFuerProdukte || [];
};

export const interesseFuerProdukteByNrSelector = (state: RootState): { [key: string]: InteresseFuerProdukte } => {
    if (state?.lookups) {
        return state.lookups?.interesseFuerProdukteByNr || {};
    }

    return defaultLookups.interesseFuerProdukteByNr || {};
};

export const erweiterteSuchenSelector = (state: RootState): ErweiterteSuche[] => {
    if (state?.lookups) {
        return state.lookups?.erweiterteSuchen || [];
    }

    return defaultLookups.erweiterteSuchen || [];
};

export const erweiterteSuchenByNrSelector = (state: RootState): { [key: string]: ErweiterteSuche } => {
    if (state?.lookups) {
        return state.lookups?.erweiterteSuchenByNr || {};
    }

    return defaultLookups.erweiterteSuchenByNr || {};
};

export const laenderSelector = (state: RootState): Land[] => {
    if (state?.lookups) {
        return state.lookups?.laender || [];
    }

    return defaultLookups.laender || [];
};

export const laenderByIkz2Selector = (state: RootState): { [key: string]: Land } => {
    if (state?.lookups) {
        return state.lookups?.laenderByIkz2 || {};
    }

    return defaultLookups.laenderByIkz2 || {};
};

export const skontodefinitionenSelector = (state: RootState): Skontodefinition[] => {
    if (state?.lookups) {
        return state.lookups?.skontodefinitionen || [];
    }

    return defaultLookups.skontodefinitionen || [];
};

export const skontodefinitionenByArtSelector = (state: RootState): { [key: string]: Skontodefinition } => {
    if (state?.lookups) {
        return state.lookups?.skontodefinitionenByArt || {};
    }

    return defaultLookups.skontodefinitionenByArt || {};
};

export const kontodefinitionenSelector = (state: RootState): Kontodefinition[] => {
    if (state?.lookups) {
        return state.lookups?.kontodefinitionen || [];
    }

    return defaultLookups.kontodefinitionen || [];
};

export const einheitenSelector = (state: RootState): Einheit[] => {
    if (state?.lookups) {
        return state.lookups?.einheiten || [];
    }

    return defaultLookups.einheiten || [];
};

export const einheitenByCodeSelector = (state: RootState): { [key: string]: Einheit } => {
    if (state?.lookups) {
        return state.lookups?.einheitenByCode || {};
    }

    return defaultLookups.einheitenByCode || {};
};

export const lagerSelector = (state: RootState): Lager[] => {
    if (state?.lookups) {
        return state.lookups?.lager || [];
    }

    return defaultLookups.lager || [];
};

export const lagerByIntNrSelector = (state: RootState): { [key: number]: Lager } => {
    if (state?.lookups) {
        return state.lookups?.lagerByIntNr || {};
    }

    return defaultLookups.lagerByIntNr || {};
};

export const araRegelungenSelector = (state: RootState): AraRegelung[] => {
    if (state?.lookups) {
        return state.lookups?.araRegelungen || [];
    }

    return defaultLookups.araRegelungen || [];
};

export const araRegelungenByArtSelector = (state: RootState): { [key: number]: AraRegelung } => {
    if (state?.lookups) {
        return state.lookups?.araRegelungenByArt || {};
    }

    return defaultLookups.araRegelungenByArt || {};
};

export const lieferantenformulareSelector = (state: RootState): Lieferantenformular[] => {
    if (state?.lookups) {
        return state.lookups?.lieferantenformulare || [];
    }

    return defaultLookups.lieferantenformulare || [];
};

export const lieferantenformulareByNrSelector = (state: RootState): { [key: number]: Lieferantenformular } => {
    if (state?.lookups) {
        return state.lookups?.lieferantenformulareByNr || {};
    }

    return defaultLookups.lieferantenformulareByNr || {};
};

export const kalkulationsblaetterSelector = (state: RootState): Kalkulationsblatt[] => {
    if (state?.lookups) {
        return state.lookups?.kalkulationsblaetter || [];
    }

    return defaultLookups.kalkulationsblaetter || [];
};

export const kalkulationsblaetterByBlattSelector = (state: RootState): { [key: string]: Kalkulationsblatt } => {
    if (state?.lookups) {
        return state.lookups?.kalkulationsblaetterByBlatt || {};
    }

    return defaultLookups.kalkulationsblaetterByBlatt || {};
};

export const artikelEigenschaftenSelector = (state: RootState): ArtikelEigenschaft[] => {
    if (state?.lookups) {
        return state.lookups?.artikelEigenschaften || [];
    }

    return defaultLookups.artikelEigenschaften || [];
};

export const artikelEigenschaftenByKzNrSelector = (
    state: RootState,
): { [key: string]: { [key: number]: ArtikelEigenschaft } } => {
    if (state?.lookups) {
        return state.lookups?.artikelEigenschaftenByKzNr || {};
    }

    return defaultLookups.artikelEigenschaftenByKzNr || {};
};

export const produktgruppenSelector = (state: RootState): Produktgruppe[] => {
    if (state?.lookups) {
        return state.lookups?.produktgruppen || [];
    }

    return defaultLookups.produktgruppen || [];
};

export const produktgruppenByNrSelector = (state: RootState): { [key: string]: Produktgruppe } => {
    if (state?.lookups) {
        return state.lookups?.produktgruppenByNr || {};
    }

    return defaultLookups.produktgruppenByNr || {};
};

export const produzentenSelector = (state: RootState): Produzent[] => {
    if (state?.lookups) {
        return state.lookups?.produzenten || [];
    }

    return defaultLookups.produzenten || [];
};

export const produzentenByNrSelector = (state: RootState): { [key: string]: Produzent } => {
    if (state?.lookups) {
        return state.lookups?.produzentenByNr || {};
    }

    return defaultLookups.produzentenByNr || {};
};

export const maschinentypenSelector = (state: RootState): Maschinentype[] => {
    if (state?.lookups) {
        return state.lookups?.maschinentypen || [];
    }

    return defaultLookups.maschinentypen || [];
};

export const maschinentypenByNrSelector = (state: RootState): { [key: string]: Maschinentype } => {
    if (state?.lookups) {
        return state.lookups?.maschinentypenByNr || {};
    }

    return defaultLookups.maschinentypenByNr || {};
};

export const lookupsLoadedSelector = (state: RootState): boolean => {
    if (!state?.lookups) {
        return false;
    }

    // return false if any lookup is still loading, otherwise return true
    return (
        Object.keys(state.lookups)
            .filter((key) => key.match(/Loaded$/))
            .find((key) => !((state.lookups as never) as { [key: string]: boolean })[key]) === undefined
    );
};

export const lookupsLoadingFinishedSelector = (state: RootState): boolean => {
    return state?.lookups?.loadingFinished || false;
};
